import { useState, useEffect } from 'react';
import type { WizardLayout } from '../types/WizardTypes';

interface UseResponsiveLayoutReturn {
  layout: WizardLayout;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

/**
 * Hook for responsive layout management
 * Determines the appropriate wizard layout based on screen size
 */
export function useResponsiveLayout(variant: 'tabs' | 'accordion' | 'auto' = 'auto'): WizardLayout {
  const [layout, setLayout] = useState<WizardLayout>(() => {
    if (variant !== 'auto') {
      return variant;
    }
    
    // Default to tabs if window is not available (SSR)
    if (typeof window === 'undefined') {
      return 'tabs';
    }
    
    // Determine initial layout based on screen size
    return window.innerWidth >= 768 ? 'tabs' : 'accordion';
  });

  useEffect(() => {
    if (variant !== 'auto') {
      setLayout(variant);
      return;
    }

    const handleResize = () => {
      const newLayout = window.innerWidth >= 768 ? 'tabs' : 'accordion';
      setLayout(newLayout);
    };

    window.addEventListener('resize', handleResize);
    
    // Set initial layout
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [variant]);

  return layout;
}